import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Book, Music, Image as ImageIcon, Code } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function CopyrightRegistration() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    {
      icon: Book,
      title: 'Literary Works',
      description: 'Protect books, articles, manuscripts, and written content.',
      image: 'https://images.unsplash.com/photo-1593444285553-28163240e3f1?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      icon: Music,
      title: 'Musical Works',
      description: 'Secure rights to songs, compositions, and sound recordings.',
      image: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      icon: ImageIcon,
      title: 'Artistic Works',
      description: 'Safeguard photographs, paintings, graphics, and visual art.',
      image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      icon: Code,
      title: 'Digital Content',
      description: 'Protect software, websites, and digital media creations.',
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
  ];

  const faqs = [
    {
      question: 'What is copyright?',
      answer: 'Copyright is a form of intellectual property protection that grants creators exclusive rights to their original works of authorship, including literary, dramatic, musical, artistic, and certain other intellectual works.',
    },
    {
      question: 'What does copyright protect?',
      answer: 'Copyright protects original works of authorship including books, music, art, photographs, movies, software, architectural works, and more. It does not protect ideas, facts, or methods of operation.',
    },
    {
      question: 'How long does copyright protection last?',
      answer: 'For works created after January 1, 1978, copyright protection typically lasts for the life of the author plus 70 years. For works made for hire, the duration is 95 years from publication or 120 years from creation, whichever is shorter.',
    },
    {
      question: 'Do I need to register my copyright?',
      answer: 'While copyright exists automatically upon creation, registration with the U.S. Copyright Office provides important legal advantages, including the ability to file infringement lawsuits and claim statutory damages.',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="copyright-registration-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="service-hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/90 via-[#06214c]/80 to-[#A0522D]/75"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="service-hero-title"
            >
              Copyright Registration
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8" data-testid="service-hero-subtitle">
              Protect your original creative works with official copyright registration. Secure your rights and prevent unauthorized use.
            </p>
            <button
              onClick={() => setShowDialog(true)}
              className="btn-primary"
              data-testid="service-hero-cta"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-abstract-bg" data-testid="service-benefits-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              What We Protect
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Comprehensive copyright protection for all types of creative works.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 fade-in"
                  data-testid={`benefit-${index}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={benefit.image} 
                      alt={benefit.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810]/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <Icon className="text-[#06214c]" size={24} />
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="section bg-[#FFF5E6]" data-testid="service-info-section">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-8 text-center" style={{ fontFamily: 'Playfair Display' }}>
              Why Copyright Registration Matters
            </h2>
            <div className="space-y-6 text-lg text-[#2C1810] leading-relaxed">
              <p>
                Copyright registration provides creators with crucial legal protections and advantages. While copyright exists automatically from the moment of creation, registering your work with the U.S. Copyright Office establishes a public record of your copyright claim and provides enhanced legal protections.
              </p>
              <p>
                With registered copyright, you can file lawsuits for infringement in federal court, claim statutory damages and attorney's fees in successful litigation, and create a public record that can deter potential infringers. Registration also allows you to record your copyright with U.S. Customs and Border Protection to prevent the importation of infringing copies.
              </p>
              <p>
                Our copyright registration service handles the entire process for you, from preparing your application to ensuring all requirements are met for successful registration. We work with authors, artists, musicians, photographers, software developers, and content creators to protect their valuable intellectual property.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-abstract-shapes" data-testid="service-faq-section">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Common questions about copyright registration.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger
                    className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]"
                    data-testid={`faq-question-${index}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]" data-testid={`faq-answer-${index}`}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialSlider autoSlide={true} />

      {/* CTA Section */}
      <section 
        className="section relative overflow-hidden" 
        data-testid="service-cta-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06214c]/95 to-[#A0522D]/95"></div>
        </div>
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Playfair Display' }}>
            Protect Your Creative Work Today
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-white/90">
            Secure your copyright and enjoy peace of mind knowing your creative works are legally protected.
          </p>
          <button
            onClick={() => setShowDialog(true)}
            className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl"
            data-testid="service-cta-btn"
          >
            Get Started Today
          </button>
        </div>
      </section>

      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="copyright_registration" />

      <Footer />
    </div>
  );
}